import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart as BarChartIcon, ArrowUpRight, ArrowDownRight, Ban, AlertCircle, TrendingDown, Percent, PhoneIncoming, PhoneOutgoing, Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { getVolumeTrends } from '@/api/analytics/api';
import type { VolumeTrendsResponse } from '@/api/analytics/types';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export interface VolumeTrendsChartProps {
    className?: string;
    startDate?: string;
    endDate?: string;
    granularity: 'day' | 'week' | 'month' | 'year';
    onGranularityChange?: (granularity: 'day' | 'week' | 'month' | 'year') => void;
    rubricId?: string;
    keyPath?: string;
    assistantId?: string;
    onError?: (error: string) => void;
}

interface CombinedChartDataPoint {
    period: string;
    inboundCount: number;
    outboundCount: number;
    totalCount: number;
    [key: string]: number | string;
}

interface TooltipProps {
    active?: boolean;
    payload?: Array<{
        value: number | string;
        name: string;
        color: string;
        dataKey: string;
        payload: CombinedChartDataPoint;
    }>;
    label?: string;
}

// Professional color palette
const CHART_COLORS = {
    inbound: 'hsl(160, 84%, 39%)',  // Emerald green
    outbound: 'hsl(217, 91%, 60%)',  // Royal blue
};

// Generate unique colors for rubric trend lines
const getRubricColor = (index: number) => {
    // HSL color generation for maximum visual distinction
    // Using golden ratio conjugate for optimal spacing around the color wheel
    const goldenRatioConjugate = 0.618033988749895;
    
    // Start with a random seed and then use golden ratio to space hues
    const hueBase = 0.27; // Arbitrary starting point (gives pleasant first colors)
    let hue = ((hueBase + index * goldenRatioConjugate) % 1) * 360;
    
    // Create different "families" of colors by varying saturation and lightness
    // This increases visual distinction beyond just hue differences
    const colorFamily = index % 4;
    
    // Default values (will be overridden by switch case)
    let saturation = 80;
    let lightness = 50;
    
    switch (colorFamily) {
        case 0: // Vibrant colors
            saturation = 85;
            lightness = 55;
            break;
        case 1: // Deeper colors
            saturation = 90;
            lightness = 35;
            break;
        case 2: // Softer colors
            saturation = 70;
            lightness = 65;
            break;
        case 3: // Rich mid-tones
            saturation = 80;
            lightness = 45;
            break;
    }
    
    // Add slight variations to avoid exact pattern repetition
    saturation += (index % 3) * 3;
    lightness += (index % 5) * 2;
    
    // Ensure lightness stays in a good range for visibility
    lightness = Math.max(30, Math.min(70, lightness));
    
    return `hsl(${Math.floor(hue)}, ${saturation}%, ${lightness}%)`;
};

const EmptyState = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="flex flex-col items-center justify-center h-[300px]"
    >
        <div className="relative">
            <div className="absolute inset-0 bg-primary/5 blur-2xl rounded-full" />
            <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl border-2 border-dashed border-primary/20 bg-background">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent rounded-2xl" />
                <div className="relative">
                    <TrendingDown className="w-7 h-7 text-primary/50" />
                </div>
            </div>
        </div>
        
        <div className="mt-6 text-center space-y-2">
            <div className="text-sm font-medium text-muted-foreground">No Volume Data Available</div>
            <div className="text-xs text-muted-foreground/80 max-w-[250px]">
                There are currently no calls recorded in the selected time period to analyze volume trends.
            </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <Ban className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Inbound</span>
            </motion.div>
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <AlertCircle className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Outbound</span>
            </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Gradient lines */}
            <div className="absolute inset-0 opacity-[0.08]"
                style={{
                    background: `
                        linear-gradient(45deg, transparent 0%, hsl(var(--primary)) 100%),
                        linear-gradient(-45deg, transparent 0%, hsl(var(--primary)) 100%)
                    `,
                    maskImage: 'radial-gradient(circle at center, transparent 50%, black 150%)'
                }}
            />
        </div>
    </motion.div>
);

const LoadingSkeleton = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="space-y-4"
    >
        {/* Chart Background Container */}
        <div className="relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background px-4 pt-3 pb-2">
            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                backgroundSize: '24px 24px'
            }} />
            
            {/* Header: Legend + Select Skeleton */}
            <div className="relative flex items-start justify-between gap-4 mb-3">
                {/* Legend Skeleton */}
                <div className="flex items-center gap-4 flex-wrap flex-1">
                    {[...Array(3)].map((_, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-2"
                        >
                            <Skeleton className="h-3 w-3 rounded-full" />
                            <Skeleton className="h-3 w-20" />
                        </motion.div>
                    ))}
                </div>
                
                {/* Granularity Select Skeleton */}
                <Skeleton className="h-8 w-[110px] shrink-0 rounded-md" />
            </div>
            
            {/* Chart Area Skeleton */}
            <div className="relative h-[300px]">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between py-2">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-3 w-8" />
                    ))}
                </div>
                
                {/* Chart lines */}
                <div className="absolute left-12 right-8 top-0 bottom-8 flex flex-col justify-between py-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-px bg-muted/30" />
                    ))}
                </div>
                
                {/* Line chart simulation */}
                <div className="absolute left-12 right-8 top-8 bottom-8">
                    {[...Array(3)].map((_, lineIndex) => (
                        <motion.div
                            key={lineIndex}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 + lineIndex * 0.1 }}
                            className="absolute inset-0"
                        >
                            <svg className="w-full h-full" preserveAspectRatio="none">
                                <motion.path
                                    d={`M 0 ${60 + lineIndex * 10} Q 25 ${40 + lineIndex * 15}, 50 ${55 + lineIndex * 8} T 100 ${50 + lineIndex * 12}`}
                                    fill="none"
                                    stroke={`hsl(var(--muted-foreground))`}
                                    strokeWidth="2"
                                    strokeOpacity={0.3 - lineIndex * 0.08}
                                    vectorEffect="non-scaling-stroke"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 1.5, delay: 0.5 + lineIndex * 0.2 }}
                                />
                            </svg>
                        </motion.div>
                    ))}
                </div>
                
                {/* X-axis labels */}
                <div className="absolute left-12 right-8 bottom-0 flex justify-between items-end h-8">
                    {[...Array(7)].map((_, i) => (
                        <Skeleton key={i} className="h-3 w-12" />
                    ))}
                </div>
            </div>
        </div>

        {/* Summary Cards Skeleton - Matches new compact design */}
        <div className="grid grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    className="rounded-lg border bg-gradient-to-br from-muted/30 to-transparent"
                >
                    <div className="flex items-center gap-3 p-3">
                        <Skeleton className="h-9 w-9 rounded-md shrink-0" />
                        <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-3 w-16" />
                            <Skeleton className="h-5 w-20" />
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

const GRANULARITY_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'year', label: 'Yearly' },
] as const;

export const VolumeAndRubricTrendsChart = ({
    className,
    startDate,
    endDate,
    granularity = 'day',
    onGranularityChange,
    rubricId,
    keyPath,
    assistantId,
    onError
}: VolumeTrendsChartProps) => {
    const [data, setData] = useState<VolumeTrendsResponse['data'] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Process and prepare the chart data
    const { chartData, rubricTrendLines } = useMemo(() => {
        if (!data?.trends) return { chartData: [], rubricTrendLines: [] };

        // Start with the volume trends data
        const combinedData: CombinedChartDataPoint[] = data.trends.map(item => ({
            ...item,
            // Add the index signature to make TypeScript happy
            [item.period]: item.period
        }));
        
        // Track rubric trend lines for rendering
        const trendLines: Array<{
            id: string;
            name: string;
            dataKey: string;
            color: string;
            type: string;
            dashArray?: string;
            index: number;
        }> = [];

        // Process rubric trends and add to the combined data
        if (data.rubricTrends) {
            const rubricTrendsArray = Array.isArray(data.rubricTrends) 
                ? data.rubricTrends 
                : [data.rubricTrends];

            // First, create the rubric trend line definitions
            rubricTrendsArray.forEach((rubric, index) => {
                if (!rubric.trend || rubric.trend.length === 0) return;
                
                // Create a data key for this rubric's success rate
                const dataKey = `${rubric.rubricId}_percentage`;
                const color = getRubricColor(index);
                
                // Add this rubric to the trend lines
                trendLines.push({
                    id: rubric.rubricId,
                    name: `${rubric.rubricName} Success Rate`,
                    dataKey,
                    color,
                    type: rubric.rubricType,
                    dashArray: '5 5',
                    index
                });
            });
            
            // Now, for each data point in combinedData, ensure all rubric dataKeys exist
            // This ensures continuous lines by providing 0% for dates with no actual data
            combinedData.forEach(dataPoint => {
                trendLines.forEach(line => {
                    if (!(line.dataKey in dataPoint)) {
                        dataPoint[line.dataKey] = 0; // Set to 0% if no data exists
                        dataPoint[`${line.id}_count`] = 0;
                        dataPoint[`${line.id}_totalCount`] = 0;
                        // Mark this as a synthetic data point (not real data)
                        dataPoint[`${line.id}_isReal`] = 0; // Use 0 to indicate synthetic
                    }
                });
            });
            
            // Finally, add the actual rubric data where it exists
            rubricTrendsArray.forEach((rubric) => {
                if (!rubric.trend || rubric.trend.length === 0) return;
                
                const dataKey = `${rubric.rubricId}_percentage`;
                
                rubric.trend.forEach(item => {
                    // Find matching period in combinedData
                    const existingDataPoint = combinedData.find(d => d.period === item.period);
                    if (existingDataPoint) {
                        // Add the rubric data to the existing data point
                        existingDataPoint[dataKey] = item.percentage;
                        // Store count and totalCount for tooltip
                        existingDataPoint[`${rubric.rubricId}_count`] = item.count;
                        existingDataPoint[`${rubric.rubricId}_totalCount`] = item.totalCount;
                        // Mark this as real data (not synthetic)
                        existingDataPoint[`${rubric.rubricId}_isReal`] = 1; // Use 1 to indicate real data
                    }
                });
            });
        }

        return { 
            chartData: combinedData,
            rubricTrendLines: trendLines
        };
    }, [data]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const params = {
                    startDate,
                    endDate,
                    granularity: granularity,
                    rubricId,
                    keyPath,
                    assistantId
                };

                const response = await getVolumeTrends(params);
                

                if (response.data) {
                    setData(response.data.data);
                    setError(null);
                } else if (response.error) {
                    throw new Error(response.error);
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch volume trends data';
                setError(message);
                onError?.(message);
                console.error('[VolumeTrends] Fetch error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate, granularity, rubricId, keyPath, assistantId]);

    const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
        if (!active || !payload || payload.length === 0 || !label) return null;
        
        // Group items by type (call volumes vs success rates)
        const volumeItems = payload.filter(p => 
            p.dataKey === 'inboundCount' || p.dataKey === 'outboundCount'
        );
        
        const rubricItems = payload.filter(p => 
            p.dataKey.includes('_percentage')
        );

        return (
            <div className="rounded-lg border-2 bg-background shadow-lg overflow-hidden min-w-[220px] max-w-[280px]">
                {/* Header - Edge to Edge */}
                <div className="bg-muted/50 px-3 py-2 border-b">
                    <p className="font-semibold text-sm">{label}</p>
                </div>
                
                <div className="px-3 py-2.5 space-y-3">
                    {/* Call Volume Section */}
                    {volumeItems.length > 0 && (
                        <div className="space-y-1.5">
                            {volumeItems.map((entry, index) => {
                                const isInbound = entry.dataKey === 'inboundCount';
                                const Icon = isInbound ? PhoneIncoming : PhoneOutgoing;
                                
                                return (
                                    <div key={index} className="flex items-center justify-between gap-3 text-xs">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <div 
                                                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: entry.color }}
                                            />
                                            <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                            <span className="text-muted-foreground truncate" title={entry.name}>
                                                {entry.name}
                                            </span>
                                        </div>
                                        <span className="font-semibold flex-shrink-0">
                                            {Number(entry.value).toLocaleString()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Rubric Success Rates Section */}
                    {rubricItems.length > 0 && (
                        <div className="space-y-1.5 pt-1.5 border-t">
                            <div className="flex items-center gap-1.5 mb-1">
                                <Percent className="h-3 w-3 text-muted-foreground" />
                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                    Success Rates
                                </span>
                            </div>
                            
                            {rubricItems.map((entry, index) => {
                                const rubricId = entry.dataKey.split('_')[0];
                                const countKey = `${rubricId}_count`;
                                const totalCountKey = `${rubricId}_totalCount`;
                                
                                const count = entry.payload[countKey];
                                const totalCount = entry.payload[totalCountKey];
                                
                                return (
                                    <div key={index} className="flex items-center justify-between gap-3 text-xs">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <div 
                                                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: entry.color }}
                                            />
                                            <span className="text-muted-foreground truncate" title={entry.name}>
                                                {entry.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <span className="font-semibold">
                                                {Number(entry.value).toFixed(1)}%
                                            </span>
                                            <span className="text-[10px] text-muted-foreground">
                                                ({count}/{totalCount})
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const hasNoData = data && (!data.trends || data.trends.length === 0 || 
        data.trends.every(item => item.inboundCount === 0 && item.outboundCount === 0));

    // Format tick values for better readability
    const formatYAxisTick = (value: number): string => {
        if (value >= 1000) {
            return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
        }
        return value.toString();
    };
    
    // Format the date on X-axis for better readability
    const formatXAxisTick = (value: string): string => {
        try {
            const date = new Date(value);
            return new Intl.DateTimeFormat('en-US', { 
                month: 'short', 
                day: 'numeric' 
            }).format(date);
        } catch {
            return value;
        }
    };

    // Create a custom Legend component with more clear representation
    const CustomLegend = () => {
        const hasRubrics = rubricTrendLines.length > 0;
        
        return (
            <div className="flex flex-wrap items-center gap-4 justify-center">
                {/* Call Volume Lines - Always centered within their container */}
                <div className={cn(
                    "flex items-center gap-6",
                    hasRubrics && "border-r pr-4"
                )}>
                    <div className="flex items-center gap-1.5">
                        <div className="w-6 h-[3px]" style={{ backgroundColor: CHART_COLORS.inbound }}></div>
                        <span className="text-xs text-muted-foreground">Inbound Calls</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-6 h-[3px]" style={{ backgroundColor: CHART_COLORS.outbound }}></div>
                        <span className="text-xs text-muted-foreground">Outbound Calls</span>
                    </div>
                </div>
                
                {/* Rubric Success Rate Lines */}
                {hasRubrics && (
                    <div className="flex flex-wrap items-center gap-4">
                        {rubricTrendLines.map((line) => (
                            <div key={line.id} className="flex items-center gap-1.5">
                                <div className="flex items-center">
                                    <div 
                                        className="w-6 h-[3px]" 
                                        style={{ 
                                            backgroundImage: `linear-gradient(to right, ${line.color} 50%, transparent 50%)`,
                                            backgroundSize: '8px 100%',
                                            opacity: 0.9
                                        }}
                                    />
                                    <div 
                                        className="w-2 h-2 rounded-full ml-0.5" 
                                        style={{ backgroundColor: line.color }}
                                    />
                                </div>
                                <span className="text-xs text-muted-foreground">
                                    {line.name}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card className={cn("relative pt-8 overflow-visible", className)}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-full blur-lg" />
                    <div className="relative bg-background/95 backdrop-blur-sm px-6 py-2 rounded-full border shadow-sm flex items-center gap-2">
                        <BarChartIcon className="h-4 w-4 text-primary/60" />
                        <span className="font-medium text-xs">Call Volume & Rubric Trends</span>
                        {isLoading && (
                            <div className="w-3 h-3 rounded-full border-2 border-primary/30 border-t-primary animate-spin ml-1" />
                        )}
                    </div>
                </div>
            </div>

            <CardContent className="pt-3">
                <AnimatePresence mode="wait">
                    {error ? (
                        <div className="flex items-center justify-center h-[200px] text-sm text-destructive">
                            {error}
                        </div>
                    ) : isLoading ? (
                        <LoadingSkeleton />
                    ) : hasNoData ? (
                        <EmptyState />
                    ) : data ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-4"
                        >
                            {/* Chart with background and legend inside */}
                            <div className="relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background px-4 pt-3 pb-2">
                                {/* Subtle Background Pattern */}
                                <div className="absolute inset-0 opacity-[0.03]" style={{
                                    backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                                    backgroundSize: '24px 24px'
                                }} />
                                
                                {/* Header: Legend (always centered) + Granularity Select (always top-right) */}
                                <div className="relative mb-4 min-h-[32px]">
                                    {/* Granularity Select - Always top right */}
                                    <div className="absolute top-0 right-0 z-10">
                                        <Select
                                            value={granularity}
                                            onValueChange={(value: 'day' | 'week' | 'month' | 'year') => {
                                                onGranularityChange?.(value);
                                            }}
                                        >
                                            <SelectTrigger className="h-8 w-[110px] text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GRANULARITY_OPTIONS.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Custom legend - always centered */}
                                    <div className="flex justify-center">
                                        <CustomLegend />
                                    </div>
                                </div>
                                
                                <div className="relative h-[300px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={chartData}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                                        >
                                            <defs>
                                                {Object.entries(CHART_COLORS).map(([key, color]) => (
                                                    <linearGradient
                                                        key={`gradient-${key}`}
                                                        id={`gradient-${key}`}
                                                        x1="0"
                                                        y1="0"
                                                        x2="0"
                                                        y2="1"
                                                    >
                                                        <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                                                        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                                                    </linearGradient>
                                                ))}
                                            </defs>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                stroke="hsl(var(--border))"
                                                strokeOpacity={0.3}
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="period"
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tickLine={false}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                                dy={10}
                                                tickFormatter={formatXAxisTick}
                                                minTickGap={15}
                                            />
                                        
                                        {/* Left Y-axis for call volumes */}
                                        <YAxis
                                            yAxisId="left"
                                            axisLine={{
                                                stroke: 'hsl(var(--border))',
                                                strokeWidth: 1,
                                                strokeOpacity: 0.3
                                            }}
                                            tickLine={false}
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                            tickFormatter={formatYAxisTick}
                                            dx={-5}
                                        />
                                        
                                        {/* Right Y-axis for percentages */}
                                        {rubricTrendLines.length > 0 && (
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                domain={[0, 100]}
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tickLine={false}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                                tickFormatter={(value) => `${value}%`}
                                                dx={5}
                                            />
                                        )}
                                        
                                        <Tooltip 
                                            content={<CustomTooltip />}
                                            cursor={{ 
                                                stroke: 'hsl(var(--muted)/50)',
                                                strokeWidth: 1,
                                                strokeDasharray: '4 4'
                                            }}
                                            wrapperStyle={{ outline: 'none' }}
                                        />
                                        
                                        {/* Inbound call line */}
                                        <Line
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="inboundCount"
                                            name="Inbound Calls"
                                            stroke={CHART_COLORS.inbound}
                                            strokeWidth={2.5}
                                            dot={false}
                                            activeDot={{
                                                r: 5,
                                                fill: CHART_COLORS.inbound,
                                                stroke: 'hsl(var(--background))',
                                                strokeWidth: 2
                                            }}
                                            fill={`url(#gradient-inbound)`}
                                        />
                                        
                                        {/* Outbound call line */}
                                        <Line
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="outboundCount"
                                            name="Outbound Calls"
                                            stroke={CHART_COLORS.outbound}
                                            strokeWidth={2.5}
                                            dot={false}
                                            activeDot={{
                                                r: 5,
                                                fill: CHART_COLORS.outbound,
                                                stroke: 'hsl(var(--background))',
                                                strokeWidth: 2
                                            }}
                                            fill={`url(#gradient-outbound)`}
                                        />
                                        
                                        {/* Add lines for each rubric trend */}
                                        {rubricTrendLines.map((line) => {
                                            // Custom dot renderer to handle overlapping points
                                            const renderDot = (dotProps: any) => {
                                                if (!dotProps || !dotProps.payload) return null;
                                                
                                                // Check if this is a real data point (1) or synthetic (0)
                                                const isReal = dotProps.payload[`${line.id}_isReal`] === 1;
                                                if (!isReal) return null;
                                                
                                                const value = dotProps.payload[line.dataKey];
                                                
                                                // Check if this overlaps with another rubric dot
                                                const hasOverlap = rubricTrendLines.some(otherLine => 
                                                    otherLine.id !== line.id && 
                                                    Math.abs(dotProps.payload[otherLine.dataKey] - value) < 0.5 &&
                                                    dotProps.payload[`${otherLine.id}_isReal`] === 1
                                                );
                                                
                                                // Apply offset only to dot position if there's overlap
                                                const offsetY = hasOverlap ? -(line.index * 1.5 + 0.5) : 0;
                                                
                                                // Render dot for real data points
                                                return (
                                                    <circle
                                                        key={`dot-${line.id}-${dotProps.index}`}
                                                        cx={dotProps.cx}
                                                        cy={dotProps.cy + offsetY}
                                                        r={5}
                                                        fill={line.color}
                                                        stroke="hsl(var(--background))"
                                                        strokeWidth={2}
                                                    />
                                                );
                                            };
                                            
                                            // Custom active dot renderer
                                            const renderActiveDot = (dotProps: any) => {
                                                if (!dotProps || !dotProps.payload) return null;
                                                
                                                const value = dotProps.payload[line.dataKey];
                                                
                                                // Check if this overlaps with another rubric dot
                                                const hasOverlap = rubricTrendLines.some(otherLine => 
                                                    otherLine.id !== line.id && 
                                                    Math.abs(dotProps.payload[otherLine.dataKey] - value) < 0.5 &&
                                                    dotProps.payload[`${otherLine.id}_isReal`] === 1
                                                );
                                                
                                                // Apply offset only to dot position if there's overlap
                                                const offsetY = hasOverlap ? -(line.index * 2 + 1) : 0;
                                                
                                                return (
                                                    <circle
                                                        key={`active-dot-${line.id}-${dotProps.index}`}
                                                        cx={dotProps.cx}
                                                        cy={dotProps.cy + offsetY}
                                                        r={6}
                                                        fill={line.color}
                                                        stroke="hsl(var(--background))"
                                                        strokeWidth={2.5}
                                                        opacity={0.9}
                                                    />
                                                );
                                            };
                                            
                                            return (
                                                <Line
                                                    key={line.id}
                                                    yAxisId="right"
                                                    type="monotone"
                                                    dataKey={line.dataKey}
                                                    name={line.name}
                                                    stroke={line.color}
                                                    strokeWidth={2}
                                                    strokeDasharray={line.dashArray}
                                                    dot={renderDot}
                                                    activeDot={renderActiveDot}
                                                    connectNulls={true}
                                                />
                                            );
                                        })}
                                    </LineChart>
                                </ResponsiveContainer>
                                </div>
                            </div>
                            
                            {/* Chart summary - Compact stats */}
                            {data.total && (
                                <div className="grid grid-cols-3 gap-3 mt-3">
                                    {/* Total Calls - Most Important */}
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 }}
                                        className="group relative overflow-hidden rounded-lg border bg-gradient-to-br from-primary/5 to-transparent hover:from-primary/10 transition-all duration-200"
                                    >
                                        <div className="flex items-center gap-3 p-3">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 ring-1 ring-primary/20">
                                                <Phone className="h-4 w-4 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground leading-none mb-1.5">Total Calls</p>
                                                <p className="text-lg font-semibold leading-none">{data.total.totalCalls.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Inbound Calls */}
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 }}
                                        className="group relative overflow-hidden rounded-lg border bg-gradient-to-br from-green-500/5 to-transparent hover:from-green-500/10 transition-all duration-200"
                                    >
                                        <div className="flex items-center gap-3 p-3">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-green-500/10 ring-1 ring-green-500/20">
                                                <PhoneIncoming className="h-4 w-4 text-green-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground leading-none mb-1.5">Inbound</p>
                                                <p className="text-lg font-semibold leading-none">{data.total.inboundPhoneCalls.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Outbound Calls */}
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="group relative overflow-hidden rounded-lg border bg-gradient-to-br from-blue-500/5 to-transparent hover:from-blue-500/10 transition-all duration-200"
                                    >
                                        <div className="flex items-center gap-3 p-3">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-500/10 ring-1 ring-blue-500/20">
                                                <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground leading-none mb-1.5">Outbound</p>
                                                <p className="text-lg font-semibold leading-none">{data.total.outboundPhoneCalls.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
};

// For backward compatibility
export const VolumeTrendsChart: React.FC<VolumeTrendsChartProps> = VolumeAndRubricTrendsChart; 